import getSymbolFromCurrency from 'currency-symbol-map';
import { TFeeInfo } from '../types/IGateway';
import { findCurrentFee } from 'utils/fee-functions/calculate-comission';
import { getGatewayFees } from './getGatewayFees';
import { defineDecimalPlaces } from 'utils/round-functions/get-decimal-places';
import InternalizationService from "../../context-components/internalization";

type FormatFeesOptions = {
    fees: TFeeInfo[] | null | undefined;
    currency?: string | null;
    decimalPlaces?: number;
    locale?: string;
    feeLabel?: string; 
};

type GatewayFeeMessageOptions = {
    gateway: any;
    currency?: string | null;
    amount: number | null | undefined;
    baseAmount?: number | string | null;
    baseCurrency?: string | null;
    displayCurrency?: string | null;
    availableCurrencies?: Array<{ code: string; rate?: number }> | null;
    isHiddenCurrencyMode?: boolean;
    decimalPlaces?: number;
    locale?: string;
    feeLabel?: string;
    noFeesText: string;
};

const i18n = new InternalizationService();
const langBack = i18n.langBack.bind(i18n);
const toNumber = (value: number | string | null | undefined): number | null => {
    if (typeof value === 'number') {
        return Number.isNaN(value) ? null : value;
    }

    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

export const getGatewayRate = (
    gateway: any,
    currency?: string | null,
    baseCurrency?: string | null,
    availableCurrencies?: Array<{ code: string; rate?: number }> | null,
): number | null => {
    if (!currency) {
        return null;
    }

    const paymentInfoRaw = gateway?.payment_information;
    const paymentInformation = Array.isArray(paymentInfoRaw) ? paymentInfoRaw : [paymentInfoRaw];
    
    const gatewayDetailsPaymentInfoRaw = gateway?.gateway_details?.payment_information;
    const gatewayDetailsPaymentInformation = Array.isArray(gatewayDetailsPaymentInfoRaw) 
        ? gatewayDetailsPaymentInfoRaw 
        : [gatewayDetailsPaymentInfoRaw];

    const findRate = (list: any[]) => {
        if (!Array.isArray(list)) return null;
        const info = list.find((item) => item?.currency === currency);
        if (info?.rate != null) return info.rate;
        
        for (const item of list) {
            if (item?.currency_list?.[currency]?.rate != null) return item.currency_list[currency].rate;
            if (item?.limits?.[currency]?.rate != null) return item.limits[currency].rate;
        }
        return null;
    };

    const gatewayBaseCurrency = gateway?.payment_information?.base_currency || gateway?.gateway_details?.payment_information?.base_currency;

    const rate = 
        findRate(paymentInformation) ?? 
        findRate(gatewayDetailsPaymentInformation) ??
        (gatewayBaseCurrency && currency === gatewayBaseCurrency ? 1 : null) ??
        availableCurrencies?.find((item) => item.code === currency)?.rate ??
        (baseCurrency && currency === baseCurrency ? 1 : null);

    return rate;
};

export const convertAmountBetweenCurrencies = (
    value: number,
    fromCurrency?: string | null,
    toCurrency?: string | null,
    gateway?: any,
    baseCurrency?: string | null,
    availableCurrencies?: Array<{ code: string; rate?: number }> | null,
): number | null => {
    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) {
        return value;
    }

    const fromRate = getGatewayRate(gateway, fromCurrency, baseCurrency, availableCurrencies);
    const toRate = getGatewayRate(gateway, toCurrency, baseCurrency, availableCurrencies);

    if (!fromRate || !toRate || fromRate <= 0 || toRate <= 0) {
        return null;
    }

    return value * (toRate / fromRate);
};

export const resolveGatewayFeeCurrency = ({
    gateway,
    currency,
    baseCurrency,
    isHiddenCurrencyMode,
}: {
    gateway: any;
    currency?: string | null;
    baseCurrency?: string | null;
    isHiddenCurrencyMode?: boolean;
}): string | null | undefined => {
    const firstCurrencyWithFees = Object.keys(gateway?.fees || {})
        .find((key) => getGatewayFees(gateway, key)?.length);

    if (isHiddenCurrencyMode && baseCurrency) {
        if (getGatewayFees(gateway, baseCurrency)?.length) {
            return baseCurrency;
        }
        if (firstCurrencyWithFees) {
            return firstCurrencyWithFees;
        }
    }

    if (currency && getGatewayFees(gateway, currency)?.length) {
        return currency;
    }

    const paymentInfoRaw = gateway?.payment_information;
    const paymentInformation = Array.isArray(paymentInfoRaw) ? paymentInfoRaw : [paymentInfoRaw];

    const infoWithFees = paymentInformation.find(info => info?.fees?.length > 0);
    if (infoWithFees?.currency) {
        return infoWithFees.currency;
    }

    const paymentInfoCurrency =
        gateway?.payment_information?.currency ||
        gateway?.gateway_details?.payment_information?.currency;

    if (paymentInfoCurrency && getGatewayFees(gateway, paymentInfoCurrency)?.length) {
        return paymentInfoCurrency;
    }

    if (firstCurrencyWithFees) {
        return firstCurrencyWithFees;
    }

    return baseCurrency || currency || paymentInfoCurrency;
};

export const convertFeesForDisplay = ({
    fees,
    sourceCurrency,
    displayCurrency,
    gateway,
    baseCurrency,
    availableCurrencies,
}: {
    fees: TFeeInfo[];
    sourceCurrency?: string | null;
    displayCurrency?: string | null;
    gateway: any;
    baseCurrency?: string | null;
    availableCurrencies?: Array<{ code: string; rate?: number }> | null;
}): TFeeInfo[] => {
    if (!sourceCurrency || !displayCurrency || sourceCurrency === displayCurrency) {
        return fees;
    }

    return fees.map((fee) => {
        const flatFeeAmount = toNumber(fee.flat_fee_amount);
        const minAmount = toNumber(fee.min_transaction_amount);
        const maxAmount = toNumber(fee.max_transaction_amount);
        const minFeeAmount = toNumber(fee.min_fee_amount);
        const maxFeeAmount = toNumber(fee.max_fee_amount);

        const convertedFlatFee = flatFeeAmount !== null ? convertAmountBetweenCurrencies(
            flatFeeAmount,
            sourceCurrency,
            displayCurrency,
            gateway,
            baseCurrency,
            availableCurrencies,
        ) : null;

        const convertedMin = minAmount !== null ? convertAmountBetweenCurrencies(
            minAmount,
            sourceCurrency,
            displayCurrency,
            gateway,
            baseCurrency,
            availableCurrencies,
        ) : null;

        const convertedMax = maxAmount !== null ? convertAmountBetweenCurrencies(
            maxAmount,
            sourceCurrency,
            displayCurrency,
            gateway,
            baseCurrency,
            availableCurrencies,
        ) : null;

        const convertedMinFee = minFeeAmount !== null ? convertAmountBetweenCurrencies(
            minFeeAmount,
            sourceCurrency,
            displayCurrency,
            gateway,
            baseCurrency,
            availableCurrencies,
        ) : null;

        const convertedMaxFee = maxFeeAmount !== null ? convertAmountBetweenCurrencies(
            maxFeeAmount,
            sourceCurrency,
            displayCurrency,
            gateway,
            baseCurrency,
            availableCurrencies,
        ) : null;

        return {
            ...fee,
            ...(convertedFlatFee !== null && { flat_fee_amount: String(convertedFlatFee) }),
            ...(convertedMin !== null && { min_transaction_amount: String(convertedMin) }),
            ...(convertedMax !== null && { max_transaction_amount: String(convertedMax) }),
            ...(convertedMinFee !== null && { min_fee_amount: convertedMinFee }),
            ...(convertedMaxFee !== null && { max_fee_amount: convertedMaxFee }),
        };
    });
};

export const formatFees = ({
    fees,
    currency,
    decimalPlaces = 2,
    locale,
    feeLabel = 'Fee',
}: FormatFeesOptions): string | null => {
    const symbol = currency ? getSymbolFromCurrency(currency) || '' : '';

    if (!fees || fees.length === 0) {
        return null;
    }

    
    const percentValues = fees
        .map((fee) => fee.percentage_fee)
        .filter((val): val is number => val !== null && val > 0);

    
    const flatValues = fees
        .map((fee) => Number(fee.flat_fee_amount))
        .filter((val) => val > 0);

    const maxFeeAmount = fees
        .map((fee) => Number(fee.max_fee_amount))
        .filter((val) => val > 0);

    const minFeeAmount = fees
        .map((fee) => Number(fee.min_fee_amount))
        .filter((val) => val > 0);

    const parts: string[] = [];
    let feeValueLimit = '';

    
    const formatFlat = (value: number) =>
        `${symbol}${value.toLocaleString(locale, {
            minimumFractionDigits: Number.isInteger(value) ? 0 : decimalPlaces,
            maximumFractionDigits: decimalPlaces,
            numberingSystem: 'latn',
        } as any)}`;

    
    if (percentValues.length > 0) {
        const min = Math.min(...percentValues);
        const max = Math.max(...percentValues);
        parts.push(min === max ? `${min}%` : `${min}-${max}%`);
    }

    
    if (flatValues.length > 0) {
        const min = Math.min(...flatValues);
        const max = Math.max(...flatValues);

        if (min === max) {
            parts.push(formatFlat(min));
        } else {
            parts.push(`${formatFlat(min)}-${formatFlat(max)}`);
        }
    }

    if (maxFeeAmount.length && minFeeAmount.length) {
        const maxValue = Math.max(...maxFeeAmount);
        const minValue = Math.min(...minFeeAmount);
        feeValueLimit = ` (${langBack('between')} ${formatFlat(minValue)} - ${formatFlat(maxValue)})`;
    }

    if (minFeeAmount.length && !maxFeeAmount.length) {
        const minValue = Math.min(...minFeeAmount);
        feeValueLimit = ` (${langBack('from')} ${formatFlat(minValue)})`
    }

    if (maxFeeAmount.length && !minFeeAmount.length) {
        const maxValue = Math.max(...maxFeeAmount);
        feeValueLimit = ` (${langBack('up to')} ${formatFlat(maxValue)})`
    }

    if (parts.length === 0) {
        return null;
    }

    return `${feeLabel} ${parts.join(' + ')}${feeValueLimit}`;
};

export const doFeesVaryByAmount = (fees: TFeeInfo[] | null | undefined): boolean => {
    if (!fees || fees.length === 0) {
        return false;
    }

    if (fees.length > 1) {
        return true;
    }

    const [fee] = fees;
    const min = toNumber(fee.min_transaction_amount) || 0;
    const max = toNumber(fee.max_transaction_amount) || 0;
    const coversAllAmounts = min === 0 && (max === 0 || max >= 999999999);

    return !coversAllAmounts;
};

export const getApplicableFees = (
    fees: TFeeInfo[] | null | undefined,
    amount: number | null | undefined,
    currency?: string | null
): TFeeInfo[] => {
    if (!fees || fees.length === 0 || amount === null || isNaN(amount) || amount <= 0) {
        return [];
    }

    const currentFee = findCurrentFee(fees, amount, currency);
    
    return currentFee ? [currentFee] : [];
};

export const getGatewayFeeMessage = ({
    gateway,
    currency,
    amount,
    baseAmount,
    baseCurrency,
    displayCurrency,
    availableCurrencies,
    isHiddenCurrencyMode = false,
    decimalPlaces,
    locale,
    feeLabel = 'Fee',
    noFeesText,
}: GatewayFeeMessageOptions): {
    feeText: string;
    isNoFees: boolean;
    allFees: TFeeInfo[];
    applicableFees: TFeeInfo[];
} => {
    const feeCurrency = resolveGatewayFeeCurrency({
        gateway,
        currency,
        baseCurrency,
        isHiddenCurrencyMode,
    });
    const allFees = (getGatewayFees(gateway, feeCurrency) || []) as TFeeInfo[];
    const numericBaseAmount = toNumber(baseAmount);
    
    const targetCurrency = (!isHiddenCurrencyMode && feeCurrency) ? feeCurrency : (displayCurrency || baseCurrency || currency);
    const rateToTarget = getGatewayRate(gateway, targetCurrency, baseCurrency, availableCurrencies);
    
    let finalDisplayCurrency = targetCurrency;
    if (rateToTarget === null) {
        const rateToBase = baseCurrency ? getGatewayRate(gateway, baseCurrency, baseCurrency, availableCurrencies) : null;
        finalDisplayCurrency = (rateToBase !== null && baseCurrency) ? baseCurrency : (feeCurrency || currency || '');
    }

    const primaryDisplayCurrency = finalDisplayCurrency;

    const resolvedDecimalPlaces = decimalPlaces ?? (primaryDisplayCurrency ? defineDecimalPlaces(primaryDisplayCurrency) : 2);

    const normalizedAllFees = convertFeesForDisplay({
        fees: allFees,
        sourceCurrency: feeCurrency,
        displayCurrency: primaryDisplayCurrency,
        gateway,
        baseCurrency,
        availableCurrencies,
    });

    const resolvedAmount = convertAmountBetweenCurrencies(
        toNumber(amount) || 0,
        baseCurrency || '',
        primaryDisplayCurrency,
        gateway,
        baseCurrency,
        availableCurrencies
    ) || (toNumber(amount) || 0);

    const applicableFees = getApplicableFees(normalizedAllFees, resolvedAmount, primaryDisplayCurrency);

    const hasEnteredAmount = resolvedAmount > 0;
    const isAmountOutOfFeeRange = hasEnteredAmount && allFees.length > 0 && applicableFees.length === 0;

    const feesToFormat = applicableFees.length > 0
        ? applicableFees
        : (allFees.length > 0 && !hasEnteredAmount ? normalizedAllFees : []);

    const feeText = isAmountOutOfFeeRange
        ? ''
        : (formatFees({
            fees: feesToFormat,
            currency: primaryDisplayCurrency,
            decimalPlaces: resolvedDecimalPlaces,
            locale,
            feeLabel,
        }) || noFeesText);

    return {
        feeText,
        isNoFees: feeText === noFeesText,
        allFees,
        applicableFees,
    };
};
